<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import _ from 'lodash';
import { verifyCodeApi, sendCodeApi } from '@Api/otp.api';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useAuthSessionStore } from '@Stores/authSession.store';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { trackClientDeviceMfaValidated } from '@Analytics/auth.mixpanel';
import { useCountdownTimer } from '@Use/useCountdownTimer';
import { useOtpCodeVerification } from '@Use/useOtpCodeVerification';
import { OtpStatus } from '@Enums/otpStatus.enum';
import { AuthenticationStatusCode } from '@Enums/authenticationStatusCode.enum';
import type { OtpDeliveryType } from '@Types/otp.interface';

import { GuidedShowcaseLayout, ActionBarLayout, formatPhoneNumber } from '@Shared';
import ShowcaseImage from '@Components/ShowcaseImage.vue';
import OtpLockedState from '@Components/OtpLockedState.vue';

// Constants
const PHONE_CODE_COOLDOWN_SECONDS = 30;

// Base data / setup
const { proxy } = getCurrentInstance();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const authSessionStore = useAuthSessionStore();
const deviceAuthStore = useDeviceAuthStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { isCountdownRunning, timeLeft, start } = useCountdownTimer(PHONE_CODE_COOLDOWN_SECONDS);
const route = useRoute();
const { lastDeliveryType } = storeToRefs(deviceAuthStore);

// Shared OTP verify surface (inline error, locked state, sending flag,
// multi-channel detection, try-again copy, channel switch, the shared post-verify
// routing, and the shared send/resend skeleton). Channel-specific bits are injected
// at call sites.
const {
    inlineError,
    codeLocked,
    isSendingCode,
    hasMultipleChannels,
    tryAnotherWay,
    handleVerifyResponse,
    performResend,
} = useOtpCodeVerification();

// Form state
const isWorking = ref(false);
const smsCode = ref('');
const context = _.isNil(route.query.context?.toString()) ? '' : route.query.context?.toString();
const trackingCodeResent = ref(false);

// Delivery-copy computed
const deliveryCopy = computed(() => {
    if (lastDeliveryType.value === 'PhoneCall') {
        return "We've sent a 6-digit security code via voice phone call to:";
    }
    // Default SMS copy (also used if lastDeliveryType is null, preserving legacy behavior)
    return "We've sent a 6-digit security code via text message to:";
});

// Identifier shown to the user. Prefer the redacted loginHint from the session
// when present (non-Payments flows). When there's no hint (e.g., Payments
// manual-entry flow), fall back to the phone number the user typed on the
// previous screen, formatted for display.
const identifierDisplay = computed(() => {
    const hint = deviceAuthStore.loginHint?.trim();
    if (hint) return hint;
    const typed = authSessionStore.phoneNumber;
    return typed ? formatPhoneNumber(typed) : '';
});

// Form navigation
const { advanceFlow, goBackInFlow } = useFlowNavigation();

// When the user entered this view via the full-login OTP handoff, hide back button.
const isFullLoginOtp = computed(() => authSessionStore.fullLoginInitiated);
const handleBackClick = () => {
    if (isFullLoginOtp.value) return;
    goBackInFlow();
};

// Actions
const resolveDeliveryTypeForResend = (type: OtpDeliveryType) => type;

const sendWithDelivery = (deliveryType: OtpDeliveryType, bypassCooldown = false) =>
    performResend({
        bypassCooldown,
        isCountdownRunning: () => isCountdownRunning.value,
        start,
        // Only forward a real phone the user typed. deviceAuthStore.loginHint is a
        // redacted display string (e.g. "(***) ***-1234") and must NEVER be sent to
        // the BFF. When we have no typed number, omit phoneNumber so the BFF resolves
        // it from the activity session hint.
        sendFn: () => sendCodeApi({
            phoneNumber: authSessionStore.phoneNumber || undefined,
            deliveryType: resolveDeliveryTypeForResend(deliveryType),
        }),
        successToast: 'An access code is being sent to your mobile device.',
        clearCode: () => { smsCode.value = ''; },
        onInitialized: () => {
            deviceAuthStore.setLastDeliveryType(deliveryType);
            trackingCodeResent.value = true;
        },
    });

const resendCode = () => {
    const type = (lastDeliveryType.value === 'PhoneCall' ? 'PhoneCall' : 'Sms') as OtpDeliveryType;
    return sendWithDelivery(type);
};

// Locked-state Resend (CodeRetriesExhausted): the current code is dead, so bypass the resend
// cooldown — forcing a 30s wait here is broken UX (there is nothing left to rate-limit).
const resendFromLocked = () => {
    const type = (lastDeliveryType.value === 'PhoneCall' ? 'PhoneCall' : 'Sms') as OtpDeliveryType;
    return sendWithDelivery(type, true);
};

const switchChannel = () => {
    const nextType: OtpDeliveryType = lastDeliveryType.value === 'PhoneCall' ? 'Sms' : 'PhoneCall';
    return sendWithDelivery(nextType);
};

const verifyCode = async () => {
    if (isWorking.value) return;
    isWorking.value = true;
    inlineError.value = null;

    try {
        const response = await verifyCodeApi({ code: smsCode.value });

        // Analytics — Auth Service decision wins. Fire when AS decided Success
        // (post-token-exchange) OR when AS made no decision (no statusCode) and
        // the BFF reported Passed (legacy / flag-off pre-token-exchange path).
        // This intentionally suppresses analytics when statusCode is set to
        // anything other than Success (e.g. 4001 AccessDenied, 4002 StepUpRequired,
        // 4003 DeviceThrottled) even if status === Passed, since the user is
        // routed away from "validated" by handleStatusCode.
        if (
            response.statusCode === AuthenticationStatusCode.Success
            || (response.statusCode == null && response.status === OtpStatus.Passed)
        ) {
            trackClientDeviceMfaValidated(
                context,
                lastDeliveryType.value === 'PhoneCall' ? 'Phone call' : 'SMS',
                trackingCodeResent.value,
            );
        }

        // Shared post-verify routing (statusCode-wins + sub-flow fallback).
        // Phone "Passed" just advances the flow.
        await handleVerifyResponse(response, {
            advanceFlow,
            clearCode: () => { smsCode.value = ''; },
            onPassed: advanceFlow,
        });
    } catch (err) {
        console.error('[Auth MFE] verifyCodeApi failed:', err);
        proxy.$apptoast('An error occurred trying to verify the passcode. Please try again.');
    } finally {
        isWorking.value = false;
    }
};

onMounted(() => {
    start();
});


</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        title="Verify your phone number"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :show-back-button="!isFullLoginOtp"
        @back-click="handleBackClick"
        :steps="['verify-phone', 'verify-code']"
        current-step="0">

        <app-form-content-layout>
            <!-- Inline locked state: current code's retries exhausted, session
                 still valid. Shown in place of the input; resend or switch
                 channel to continue. -->
            <otp-locked-state
                v-if="codeLocked"
                :has-multiple-channels="hasMultipleChannels"
                :is-sending="isSendingCode"
                @resend="resendFromLocked"
                @try-another-way="tryAnotherWay" />

            <template v-else>
                <p class="verify-passcode-view__delivery-copy">
                    {{ deliveryCopy }} <span data-testid="redacted-identifier">{{ identifierDisplay }}</span>
                </p>

                <app-passcode-input
                    v-model="smsCode"
                    class="verify-passcode-view__passcode"
                    auto-focus />

                <p v-if="inlineError" class="verify-passcode-view__error" role="alert" data-testid="inline-error">
                    {{ inlineError }}
                </p>

                <div class="verify-passcode-view__resend">
                    <span v-if="isCountdownRunning" class="verify-passcode-view__countdown">
                        Resend in {{ timeLeft }} seconds
                    </span>
                    <app-link
                        v-else
                        emphasized
                        data-testid="resend-link"
                        @onClick="resendCode">
                        {{ lastDeliveryType === 'PhoneCall' ? 'Call again' : 'Resend code' }}
                    </app-link>
                </div>

                <div v-if="!isCountdownRunning" class="verify-passcode-view__switch">
                    <span v-if="lastDeliveryType === 'PhoneCall'">Didn't receive a call? </span>
                    <span v-else>Didn't receive the code? </span>
                    <app-link
                        v-if="!hasMultipleChannels"
                        emphasized
                        data-testid="switch-channel-link"
                        @onClick="switchChannel">
                        {{ lastDeliveryType === 'PhoneCall' ? 'send a text' : 'Get a call' }}
                    </app-link>
                    <app-link
                        v-else
                        emphasized
                        data-testid="try-another-way-link"
                        @onClick="tryAnotherWay">
                        Try another way
                    </app-link>
                </div>
            </template>

        </app-form-content-layout>

        <action-bar-layout v-if="!codeLocked" @back-click="handleBackClick">
            <app-button
                expanded
                size="large"
                :is-loading="isWorking"
                data-id="continue"
                @onClick="verifyCode">
                    Continue
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>

    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.verify-passcode-view {

    &__delivery-copy {
        line-height: 1.5;
    }

    // Center the passcode row within the form column and cap its width so
    // cells stay legible on desktop. AppPasscodeInput defaults to
    // `width: fit-content` with fixed-size cells; we stretch its inner flex
    // container, cap the wrapper width, and bound each cell.
    &__passcode {
        max-width: 22rem;
        margin-inline: auto;

        :deep(.app-passcode-input) {
            width: 100%;
            justify-content: center;
        }

        :deep(.app-passcode-input .digit-box) {
            flex: 1;
            max-width: 3.25rem;
        }
    }

    &__resend,
    &__switch {
        font-size: 0.95rem;
        font-weight: 400;
        line-height: 1.5;
    }

    &__countdown {
        color: $black-med;
    }

    &__error {
        color: $red;
        margin: 0.5rem 0;
    }
}
</style>
